import React from 'react';
import {
  ClipCommentFragment,
  ClipCommentFragmentDoc,
} from '../../fragments.generated';
import {
  useUpdateClipCommentMutation,
  useDeleteClipCommentMutation,
  useMarkClipCommentAsViewedMutation,
  useMarkClipCommentAsUnviewedMutation,
  useCreateClipCommentMutation,
  useMarkAllClipCommentsAsViewedMutation,
  ClipCommentsQuery,
  ClipCommentsDocument,
} from '../operation.generated';
import { toast } from '../../Toaster';
import { useApolloCacheUtilities } from '../../../util/apolloUtils';
import { getApolloMutationErrorMessage } from '../../../util/apolloError';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import useSRAnnouncer from '../../../lib/useSRAnnouncer';

type ClipCommentEdge = ClipCommentsQuery['comments']['edges'][0];

interface useCommentActionsProps {
  projectId: string;
  clipId: string;
  setErrors: React.Dispatch<
    React.SetStateAction<Record<string, string | undefined>>
  >;
}
/**
 * Helper hook for sandbox purposes
 */
const useCommentActions = (props: useCommentActionsProps) => {
  const { projectId, clipId, setErrors } = props;
  const [eventTracking] = useEventsContext();
  const { announce } = useSRAnnouncer();
  const { identityInCache } = useApolloCacheUtilities();
  const [updateClipComment] = useUpdateClipCommentMutation();
  const [deleteClipComment] = useDeleteClipCommentMutation();
  const [markClipCommentAsViewed] = useMarkClipCommentAsViewedMutation();
  const [markClipCommentAsUnviewed] = useMarkClipCommentAsUnviewedMutation();
  const [createClipComment] = useCreateClipCommentMutation();
  const [markAllClipCommentsAsViewed] =
    useMarkAllClipCommentsAsViewedMutation();

  const handleSave = React.useCallback(
    async (id: string, value: string) => {
      try {
        await updateClipComment({
          variables: {
            inputs: {
              commentId: id,
              clipId,
              content: value,
            },
          },
          update: (cache, { data: mutationData }) => {
            if (!mutationData?.updateClipComment.success) {
              setErrors(prev => ({ ...prev, [id]: 'Failed to edit comment' }));
              /**
               * Throw error to trigger promise reject flow
               * that parent component is expecting
               */
              throw new Error('Failed to edit comment');
            } else if (mutationData.updateClipComment.comment) {
              cache.writeFragment<ClipCommentFragment>({
                id: identityInCache('ClipComment', id),
                fragment: ClipCommentFragmentDoc,
                data: {
                  ...mutationData.updateClipComment.comment,
                },
              });
              setErrors(prev => ({ ...prev, [id]: '' }));
            }
          },
        });
        eventTracking.track({
          type: EventName.CommentEdited,
          data: {
            type: 'clip',
            projectId,
            clipId,
          },
        });
      } catch (error) {
        const message = getApolloMutationErrorMessage(
          error,
          'Failed to edit comment'
        );
        setErrors(prev => ({ ...prev, [id]: message }));
        throw new Error(message);
      }
    },
    [
      clipId,
      eventTracking,
      identityInCache,
      projectId,
      setErrors,
      updateClipComment,
    ]
  );

  const handleCancel = React.useCallback(
    (id: string) => {
      setErrors(prev => ({ ...prev, [id]: '' }));
    },
    [setErrors]
  );

  const handleViewed = React.useCallback(
    async (id: string, markAsViewed: boolean) => {
      if (markAsViewed) {
        try {
          await markClipCommentAsViewed({
            variables: {
              inputs: { commentId: id, clipId },
            },
            optimisticResponse: () => {
              return {
                markClipCommentAsViewed: {
                  code: 'graphql_mutation_success',
                  success: true,
                  message: 'Comment marked as viewed successfully',
                },
              };
            },
            update: (cache, { data: mutationData }) => {
              if (!mutationData?.markClipCommentAsViewed.success) {
                toast({
                  message: 'Failed to mark comment as read',
                  type: 'error',
                });
              } else {
                cache.modify({
                  id: identityInCache('ClipComment', id),
                  fields: {
                    selfViewed() {
                      return true;
                    },
                  },
                });
                /**
                 * Update unviewed comment count in clip
                 */
                cache.modify({
                  id: identityInCache('Clip', clipId),
                  fields: {
                    unviewedCommentCount(cached: number) {
                      return Math.max(0, cached - 1);
                    },
                  },
                });
              }
            },
          });
          eventTracking.track({
            type: EventName.CommentViewed,
            data: {
              type: 'clip',
              projectId,
              clipId,
              viewed: true,
            },
          });
        } catch (error) {
          toast({
            message: getApolloMutationErrorMessage(
              error,
              'Failed to mark comment as read'
            ),
            type: 'error',
          });
        }
      } else {
        try {
          await markClipCommentAsUnviewed({
            variables: { inputs: { clipId, commentId: id } },
            optimisticResponse: () => {
              return {
                markClipCommentAsUnviewed: {
                  code: 'graphql_mutation_success',
                  success: true,
                  message: 'Comment marked as unviewed successfully',
                },
              };
            },
            update: (cache, { data: mutationData }) => {
              if (!mutationData?.markClipCommentAsUnviewed.success) {
                toast({
                  message: 'Failed to mark comment as unread',
                  type: 'error',
                });
              } else {
                cache.modify({
                  id: identityInCache('ClipComment', id),
                  fields: {
                    selfViewed(cached) {
                      return false;
                    },
                  },
                });
                /**
                 * Update unviewed comment count in clip
                 */
                cache.modify({
                  id: identityInCache('Clip', clipId),
                  fields: {
                    unviewedCommentCount(cached: number) {
                      return cached + 1;
                    },
                  },
                });
              }
            },
          });
          eventTracking.track({
            type: EventName.CommentViewed,
            data: {
              type: 'clip',
              projectId,
              clipId,
              viewed: false,
            },
          });
        } catch (error) {
          toast({
            message: getApolloMutationErrorMessage(
              error,
              'Failed to mark comment as unread'
            ),
            type: 'error',
          });
        }
      }
    },
    [
      markClipCommentAsViewed,
      clipId,
      identityInCache,
      eventTracking,
      projectId,
      markClipCommentAsUnviewed,
    ]
  );

  const handleDelete = React.useCallback(
    async (id: string) => {
      let updateCount = 0;
      try {
        await deleteClipComment({
          variables: {
            inputs: { clipId, commentId: id },
          },
          optimisticResponse: ({ inputs: { commentId } }) => {
            return {
              deleteClipComment: {
                code: 'graphql_mutation_success',
                success: true,
                message: 'Comment deleted successfully',
                commentId,
              },
            };
          },
          update: (cache, { data: mutationData }) => {
            updateCount += 1;
            const mutationCompleted = updateCount === 2;
            if (!mutationData?.deleteClipComment.success) {
              toast({
                message: 'Failed to delete comment',
                type: 'error',
              });
            } else {
              cache.modify({
                id: identityInCache('ClipComment', id),
                fields: {
                  deletedAt() {
                    return String(Date.now());
                  },
                },
              });
              /**
               * Update comment count in clip
               */
              cache.modify({
                id: identityInCache('Clip', clipId),
                fields: {
                  commentCount(cached: number) {
                    return Math.max(0, cached - 1);
                  },
                },
              });
              /**
               * TODO: Edge case - Update unviewed comment count in clip if
               * deleted comment was unviewed.
               */
              if (mutationCompleted) {
                toast({
                  message: 'Comment deleted',
                  type: 'success',
                });
                eventTracking.track({
                  type: EventName.CommentDeleted,
                  data: {
                    type: 'clip',
                    projectId,
                    clipId,
                  },
                });
              }
            }
          },
        });
      } catch (error) {
        toast({
          message: getApolloMutationErrorMessage(
            error,
            'Failed to delete comment'
          ),
          type: 'error',
        });
      }
    },
    [clipId, deleteClipComment, eventTracking, identityInCache, projectId]
  );

  const handleMarkAll = React.useCallback(async () => {
    try {
      await markAllClipCommentsAsViewed({
        variables: {
          inputs: { clipId },
        },
        optimisticResponse: () => {
          return {
            markAllClipCommentsAsViewed: {
              code: 'graphql_mutation_success',
              success: true,
              message: 'All comments marked as viewed successfully',
            },
          };
        },
        update: (cache, { data: mutationData }) => {
          if (!mutationData?.markAllClipCommentsAsViewed.success) {
            toast({
              message: 'Failed to mark all comments as read',
              type: 'error',
            });
          } else {
            const cacheResult = cache.readQuery<ClipCommentsQuery>({
              query: ClipCommentsDocument,
              variables: {
                inputs: {
                  clipId,
                },
              },
            });
            if (cacheResult) {
              const updatedEdges = cacheResult.comments.edges.map(edge => ({
                ...edge,
                node: {
                  ...edge.node,
                  selfViewed: true,
                },
              }));

              cache.writeQuery<ClipCommentsQuery>({
                query: ClipCommentsDocument,
                data: {
                  comments: {
                    ...cacheResult.comments,
                    edges: updatedEdges,
                  },
                },
              });

              /**
               * Update unviewed comment count in clip
               */
              cache.modify({
                id: identityInCache('Clip', clipId),
                fields: {
                  unviewedCommentCount() {
                    return 0;
                  },
                },
              });
            }
          }
        },
      });
      eventTracking.track({
        type: EventName.CommentMarkAllAsViewed,
        data: {
          type: 'clip',
          projectId,
          clipId,
        },
      });
    } catch (error) {
      toast({
        message: getApolloMutationErrorMessage(
          error,
          'Failed to mark all comments as read'
        ),
        type: 'error',
      });
    }
  }, [
    clipId,
    eventTracking,
    identityInCache,
    markAllClipCommentsAsViewed,
    projectId,
  ]);

  const handleSend = React.useCallback(
    async (text: string) => {
      try {
        await createClipComment({
          variables: {
            inputs: { clipId, content: text },
          },
          update: (cache, { data: mutationData }) => {
            if (!mutationData?.createClipComment.success) {
              /**
               * Throw error to trigger promise reject flow
               * that parent component is expecting
               */
              throw new Error(mutationData?.createClipComment.code);
            } else {
              const cacheResult = cache.readQuery<ClipCommentsQuery>({
                query: ClipCommentsDocument,
                variables: {
                  inputs: {
                    clipId,
                  },
                },
              });

              if (mutationData?.createClipComment.comment) {
                const newCommentEdge: ClipCommentEdge = {
                  __typename: 'ClipCommentEdge',
                  cursor: '', // ???
                  node: {
                    __typename: 'ClipComment',
                    ...mutationData?.createClipComment.comment,
                  },
                };

                if (cacheResult) {
                  cache.writeQuery<ClipCommentsQuery>({
                    query: ClipCommentsDocument,
                    variables: {
                      inputs: {
                        clipId,
                      },
                    },
                    data: {
                      comments: {
                        ...cacheResult.comments,
                        edges: [newCommentEdge, ...cacheResult.comments.edges],
                      },
                    },
                  });
                }

                /**
                 * Update comment count in clip
                 */
                cache.modify({
                  id: identityInCache('Clip', clipId),
                  fields: {
                    commentCount(cached: number) {
                      return cached + 1;
                    },
                  },
                });
              }
            }
          },
        });
        eventTracking.track({
          type: EventName.CommentCreated,
          data: {
            type: 'clip',
            projectId,
            clipId,
          },
        });
        announce('Comment added successfully.');
      } catch (error) {
        const message = getApolloMutationErrorMessage(
          error,
          'Failed to create comment'
        );
        toast({
          message,
          type: 'error',
        });
        throw new Error(message);
      }
    },
    [
      createClipComment,
      clipId,
      identityInCache,
      eventTracking,
      projectId,
      announce,
    ]
  );

  return {
    onSave: handleSave,
    onCancel: handleCancel,
    onDelete: handleDelete,
    onViewed: handleViewed,
    onMarkAllAsViewed: handleMarkAll,
    onSend: handleSend,
  };
};

export default useCommentActions;
